import { ajax } from "lib/observable";
import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import { PAGE_PRODUCTS, PRODUCT_DATA, PRODUCTS } from "constants/action-types";
import { Items as ProductsSchema } from "schemas/items";
import siteConfig from "site-config";
import { updateEntities } from "redux-query";
import { normalizeData } from "helpers/data/normalize";
import me from "models/me";
import { IStore } from "reducers";
import { fetchPageProducts } from "actions/products";
import { fetch as fetchVariants, getVariantsList } from "actions/variants";
import { baseActionEpic, IRequestAction } from "../base-epic";
import { add as snackbarAdd } from "../../actions/snackbars";
import { format, format as t } from "../../lib/globalize-util";
import { pickGrpcError } from "../../helpers/epics";

export const fetchProductsEpic = (action$, store) =>
    action$.ofType(PRODUCTS.FETCH.START).switchMap(({ payload, options = {}, meta }) => {
        return fetchProducts({
            store: store.getState(),
            meta,
            payload: { filters: meta.nextPage || payload },
            options,
        });
    });

export const fetchPageProductsEpic = (action$, store) => {
    return action$
        .ofType(PAGE_PRODUCTS.FETCH.START)
        .mergeMap(({ payload, meta }: ReturnType<typeof fetchPageProducts>) => {
            return fetchProducts({
                store: store.getState(),
                meta,
                payload: { filters: payload },
                options: { forceReplace: false },
            });
        });
};

export const fetchProductsByIdsEpic = (action$, store) => {
    return action$.ofType(PRODUCTS.FETCH_BY_IDS).switchMap(({ payload }) => {
        return fetchProductsByIds({
            store: store.getState() ?? {},
            ids: payload,
        });
    });
};

interface IFetchArgs {
    store: IStore;
    meta: { cacheKey?: string; [key: string]: string };
    payload: { filters: Record<string, string | boolean | number> };
    options: {
        forceReplace?: boolean;
        preview?: boolean;
    };
}
const fetchProducts = ({ options, payload, meta, store }: IFetchArgs) => {
    const { scope, entities, routing } = store;
    const { forceReplace = undefined, preview = false } = options;
    return ajax({ url: siteConfig.api + "/api/v3.0/query/items", method: "POST", body: payload.filters })
        .flatMap(({ response, xhr }) => {
            const nextPage = response.nextPagePostBody;
            response = response.data || response;
            const data = normalizeData(response, ProductsSchema, entities.schemas, scope);
            if (payload?.filters?.__search) {
                const eventParams = { c: "product", a: "search" };
                const customDimensions = {
                    term: payload.filters.__search,
                    search_origin_page: routing.locationBeforeTransitions.pathname,
                    brand_id: scope.brand._id,
                };
                me.nuAnalytics("event", [eventParams, customDimensions]);
            }

            const collectionKey = preview ? "itemsPreview" : "items";

            return [
                updateEntities({
                    [collectionKey]: (currentProducts) => ({
                        ...currentProducts,
                        ...data.entities.items,
                    }),
                }),
                fetchVariants(getVariantsList(data.entities.items)),
                {
                    meta: { ...meta, replace: forceReplace ?? !meta.nextPage },
                    payload: { results: data.result, nextPage },
                    type: PRODUCTS.FETCH.COMPLETED,
                },
            ];
        })
        .catch((error) =>
            Observable.merge(
                Observable.of({
                    error: true,
                    meta,
                    payload: error,
                    type: PRODUCTS.FETCH.COMPLETED,
                }),
            ),
        );
};

interface IFetchByIdsArgs {
    store: IStore;
    ids: string[];
}
const fetchProductsByIds = ({ store, ids }: IFetchByIdsArgs) => {
    const { scope, entities } = store;

    const body = {
        __advanced_pricing_currency_code: scope.brand?.getAdvancedPricingCurrencyCode(scope),
        __active_pricesheet: me._activePriceTemplate,
        __fields: "__all",
        __limit: 1000,
        __populate: ["__variant", "__promotions"],
        _id: ids,
    };

    return ajax({ url: `${siteConfig.api}/api/v3.0/query/items`, method: "POST", body }).flatMap(({ response }) => {
        const data = normalizeData(response, ProductsSchema, entities.schemas, scope);
        return [
            updateEntities({
                items: (currentProducts) => {
                    return {
                        ...currentProducts,
                        ...data.entities.items,
                    };
                },
            }),
        ];
    });
};

export const archiveProducts = baseActionEpic<IRequestAction>(
    PRODUCT_DATA.ARCHIVE.START,
    ({ payload }) => ({ method: "DELETE", url: siteConfig.api + payload.url }),
    () => [snackbarAdd({ message: t("All products were archived successfully") })],
    {
        errorActions: (error) => [
            snackbarAdd({
                message: format("Error while archiving products: |errorMessage|", {
                    errorMessage: pickGrpcError(error) || format("Unknown"),
                }),
            }),
        ],
    },
);
